import { IconInfoCircle } from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { ModalNotStyled } from 'ui-kit';

import { LoanDatatableLoan as Loan } from '../../features/drilldown/LoanDatatable/__generated__/fragments';
import { IconButton } from '../Buttons';
import { Checkbox } from '../Checkbox';
import { RadioButton } from '../RadioButton';
import { DetailsCard } from './DetailsCard';

export const radioButtonSelectionColumn: ColumnDef<unknown> = {
  id: 'selection',
  size: 40,
  cell: (cellContext) => (
    // Mirror createCheckBoxSelectionColumn's stopPropagation wrapper: the
    // RadioButton's onClick now lives on its full-width outer div, so a
    // bare cell click would fire both the row's selection toggle and the
    // table's row-expansion handler (handleRowClick →
    // getToggleExpandedHandler).
    <div onClick={(e) => e.stopPropagation()}>
      <RadioButton
        label={''}
        variant={cellContext.row.getIsSelected() ? 'default' : 'primary'}
        checked={cellContext.row.getIsSelected()}
        onChange={cellContext.row.getToggleSelectedHandler()}
        data-testid={`loan-row-select-radio-${cellContext.row.id}`}
      />
    </div>
  ),
  meta: {
    headerNoPill: true,
    isDraggable: false,
    canResize: false,
  },
};

export const createCheckBoxSelectionColumn = (
  onHeaderCheckboxChange: (checked: boolean) => void,
  isSelectionInfoIcon: boolean,
  isCre: boolean,
  handleCREClick?: (s: string) => void,
  rowCheckboxTestId = 'loan-row-select-checkbox',
  selectAllTestId = 'loan-table-select-all-checkbox',
): ColumnDef<Loan> => {
  return {
    id: 'selection',
    size: 40,
    cell: (cellContext) => {
      const loan = cellContext.row.original;
      const account_id = loan?.account_id || '';

      const handleOpenCREModal = () => {
        handleCREClick && handleCREClick(account_id);
      };

      // Checkbox-only cells get a Gmail-style full-<td> toggle (renderCell, via
      // meta.fullCellSelect): the checkbox is presentational and its click
      // bubbles to the td. When an info icon shares the cell, the cell-wide
      // toggle would hijack the icon — so there the checkbox stays interactive
      // and the wrapper stops row-nav itself.
      const fullCell = !isSelectionInfoIcon;
      return (
        <div
          className="flex h-full items-center gap-4"
          onClick={fullCell ? undefined : (e) => e.stopPropagation()}
        >
          <Checkbox
            checked={cellContext.row.getIsSelected()}
            onChange={fullCell ? () => undefined : cellContext.row.getToggleSelectedHandler()}
            disabled={!cellContext.row.getCanSelect()}
            data-testid={rowCheckboxTestId}
          />
          {isSelectionInfoIcon && (
            <ModalNotStyled
              trigger={
                <div data-testid={`loan-row-info-button-${account_id}`}>
                  <IconButton
                    variant="table-info"
                    className="h-9 w-9"
                    icon={<IconInfoCircle color="#757575" height="24px" />}
                  />
                </div>
              }
            >
              {({ closeModal }) => (
                <DetailsCard
                  selectedLoanId={account_id}
                  companyId={loan?.company?.id || ''}
                  onClose={closeModal}
                  isCre={isCre}
                  handleCREClick={() => {
                    handleOpenCREModal();
                    closeModal();
                  }}
                />
              )}
            </ModalNotStyled>
          )}
        </div>
      );
    },
    header: (headerContext) => {
      if (headerContext.table.options.enableMultiRowSelection !== false) {
        const originalOnChangeHandler = headerContext.table.getToggleAllRowsSelectedHandler();

        const handleChange = (checked: boolean) => {
          // const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          // Call the original onChange handler from TanStack Table
          originalOnChangeHandler({
            target: {
              checked,
            },
          });
          onHeaderCheckboxChange(checked);
        };
        const isChecked = headerContext.table.getIsAllRowsSelected();
        const indeterminate = headerContext.table.getIsSomeRowsSelected();

        return (
          <Checkbox
            checked={isChecked || indeterminate}
            icon="minus"
            onChange={() => handleChange(!isChecked)}
            data-testid={selectAllTestId}
          />
        );
      }
      return null;
    },
    meta: {
      headerNoPill: true,
      isDraggable: false,
      // Full-cell toggle only when the checkbox owns the cell alone; with an
      // info icon present the cell-wide handler would also fire on the icon.
      fullCellSelect: !isSelectionInfoIcon,
    },
  };
};

export const createInfoIconColumn = (handleCREClick: (s: string) => void): ColumnDef<Loan> => {
  return {
    id: 'selection',
    size: 40,
    cell: (cellContext) => {
      const loan = cellContext.row.original;
      const account_id = loan?.account_id || '';

      const handleOpenCREModal = () => {
        handleCREClick && handleCREClick(account_id);
      };

      return (
        <div className="flex gap-4" onClick={(e) => e.stopPropagation()}>
          <IconButton
            variant="table-info"
            className="h-9 w-9"
            icon={<IconInfoCircle color="#757575" height="24px" />}
            onClick={handleOpenCREModal}
            data-testid={`loan-row-info-button-${account_id}`}
          />
        </div>
      );
    },
    header: () => {
      return null;
    },
    meta: {
      headerNoPill: true,
      isDraggable: false,
    },
  };
};
